import React, { useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Play, Pause } from 'lucide-react'
import { usePomodoroStore } from '@turnifly/shared/stores'

/**
 * Floating mini timer that appears when user scrolls away from pomodoro tab
 * or when Picture-in-Picture is activated.
 * On desktop: uses Document PiP API (Chrome 116+) if available.
 * Fallback: floating pill within the app.
 */
export const PomodoroMiniPlayer: React.FC = () => {
  const status = usePomodoroStore((s) => s.status)
  const timeRemaining = usePomodoroStore((s) => s.timeRemaining)
  const mode = usePomodoroStore((s) => s.mode)
  const isPipActive = usePomodoroStore((s) => s.isPipActive)
  const setPipActive = usePomodoroStore((s) => s.setPipActive)
  const pauseTimer = usePomodoroStore((s) => s.pauseTimer)
  const resumeTimer = usePomodoroStore((s) => s.resumeTimer)

  const isTimerActive = status === 'running' || status === 'paused'

  // Floating pill visibility — show when timer is active and PiP requested
  const showFloating = isTimerActive && isPipActive

  const formattedTime = formatTime(timeRemaining)

  const modeColor = mode === 'work'
    ? 'bg-red-500/80'
    : mode === 'short_break'
      ? 'bg-teal-500/80'
      : 'bg-blue-500/80'

  const handleTogglePlay = useCallback((): void => {
    if (status === 'running') pauseTimer()
    else resumeTimer()
  }, [status, pauseTimer, resumeTimer])

  const handleClose = useCallback((): void => {
    setPipActive(false)
  }, [setPipActive])

  // Try native Document PiP on desktop
  const handlePipToggle = useCallback(async (): Promise<void> => {
    if (!isTimerActive) return

    // Check if Document PiP API is available
    if ('documentPictureInPicture' in window && !isPipActive) {
      try {
        const pipApi = window.documentPictureInPicture as DocumentPictureInPicture
        const pipWindow = await pipApi.requestWindow({
          width: 300,
          height: 200,
        })

        // Copy styles to PiP window
        const styleSheets = document.querySelectorAll('link[rel="stylesheet"], style')
        styleSheets.forEach((sheet) => {
          pipWindow.document.head.appendChild(sheet.cloneNode(true))
        })

        // Create simple timer display in PiP
        const container = pipWindow.document.createElement('div')
        container.style.cssText = `
          display: flex; align-items: center; justify-content: center;
          height: 100vh; background: #0e1621; color: white;
          font-family: system-ui; flex-direction: column; gap: 8px;
        `

        const timeEl = pipWindow.document.createElement('div')
        timeEl.style.cssText = 'font-size: 48px; font-weight: bold; font-variant-numeric: tabular-nums;'
        timeEl.id = 'pip-time'

        const modeEl = pipWindow.document.createElement('div')
        modeEl.style.cssText = 'font-size: 12px; opacity: 0.6; text-transform: uppercase;'

        container.appendChild(timeEl)
        container.appendChild(modeEl)
        pipWindow.document.body.appendChild(container)

        // Update PiP timer
        const updatePip = (): void => {
          const state = usePomodoroStore.getState()
          timeEl.textContent = formatTime(state.timeRemaining)
          modeEl.textContent = state.mode.replace('_', ' ')
        }

        updatePip()
        const interval = setInterval(updatePip, 1000)

        pipWindow.addEventListener('pagehide', () => {
          clearInterval(interval)
          setPipActive(false)
        })

        setPipActive(true)
        return
      } catch {
        // PiP failed, fall through to floating pill
      }
    }

    // Toggle floating pill as fallback
    setPipActive(!isPipActive)
  }, [isTimerActive, isPipActive, setPipActive])

  // Expose PiP toggle globally for the toolbar button
  useEffect(() => {
    const win = window as unknown as Record<string, unknown>
    win.__pomodoroTogglePip = handlePipToggle
    return () => { delete win.__pomodoroTogglePip }
  }, [handlePipToggle])

  if (!showFloating) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.9 }}
        drag
        dragConstraints={{ left: -150, right: 150, top: -300, bottom: 100 }}
        className={`fixed bottom-24 right-4 z-[60] flex items-center gap-2 px-4 py-2.5 rounded-full ${modeColor} backdrop-blur-xl shadow-lg cursor-grab active:cursor-grabbing`}
      >
        <span className="text-white font-bold text-lg tabular-nums">{formattedTime}</span>

        <button onClick={handleTogglePlay} className="p-1 tap-highlight-transparent">
          {status === 'running' ? (
            <Pause size={16} fill="white" className="text-white" />
          ) : (
            <Play size={16} fill="white" className="text-white" />
          )}
        </button>

        <button onClick={handleClose} className="p-1 tap-highlight-transparent">
          <X size={14} className="text-white/60" />
        </button>
      </motion.div>
    </AnimatePresence>
  )
}

// ── Utility ──

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

// ── PiP type augmentation ──

interface DocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>
}

declare global {
  interface Window {
    documentPictureInPicture?: DocumentPictureInPicture
  }
}
